/**
 * Shared analytics utilities for custom tracking events.
 *
 * The Design System handles its own component-level analytics
 * (button clicks, link clicks, etc.) via its built-in utag
 * integration. These helpers are for custom app-level events
 * that the DS doesn't cover — search, filtering, pagination, etc.
 *
 * Usage:
 *
 *   import { sendEvent, isTrackingEnabled } from '@shared/analytics';
 *
 *   if (isTrackingEnabled()) {
 *     sendEvent({ event_name: 'search_initiated', ... });
 *   }
 */

import type { AnalyticsEvent } from '@shared/types';

/**
 * Check whether Tealium tracking is enabled.
 * Returns true if drupalSettings.tealiumiq is truthy and
 * window.utag is available.
 */
export function isTrackingEnabled(): boolean {
  return !!(window.drupalSettings?.tealiumiq && window.utag);
}

/**
 * Send a utag.link event. No-ops if tracking is disabled.
 */
export function sendEvent(event: AnalyticsEvent): void {
  if (isTrackingEnabled()) {
    window.utag!.link?.(event);
  }
}

/**
 * Track a search initiated event.
 */
export function trackSearchInitiated(params: {
  searchType: string;
  resultCount: number;
  searchTerm: string;
  searchTermType?: string;
}): void {
  sendEvent({
    event_name: 'search_initiated',
    internal_search_type: params.searchType,
    search_result_count: params.resultCount,
    search_term: params.searchTerm,
    search_term_type: params.searchTermType ?? '',
  });
}

/**
 * Track filters applied event.
 */
export function trackFiltersApplied(params: {
  filterCategoriesUsed: string;
  totalResults: number;
  countBeforeFilter: number;
  countAfterFilter: number;
}): void {
  sendEvent({
    event_name: 'filters_applied',
    filter_categories_used: params.filterCategoriesUsed,
    search_result_count: params.totalResults,
    results_count_prior_to_filtering: params.countBeforeFilter,
    results_count_after_filtering: params.countAfterFilter,
  });
}

/**
 * Track an internal link click not covered by the Design System.
 */
export function trackInternalLinkClicked(params: {
  text: string;
  url: string;
  parentHeading: string;
  parentComponentType: string;
}): void {
  sendEvent({
    event_name: 'internal_link_clicked',
    link_url: params.url,
    link_type: 'link_other',
    parent_component_heading: params.parentHeading,
    parent_component_type: params.parentComponentType,
    text: params.text,
  });
}
